import { useCallback, useEffect, useRef, useState } from 'react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { RESOLVE_COMMENTS_LIMIT } from '../../../CommunityFilters/useResolveCommentsDialog'
import { useCommentsCounts } from '../../../hooks/comments/useCommentsCounts'
import { getOneMonthAgoIso } from '../../../util/dateHelpers'
import { useStableArray } from '../../../util/useStableArray'

type UseOldCommentsCheckParams = {
  channelIds: string[]
}

/** A channel that has unreplied comments older than one month. */
export type ChannelWithOldComments = {
  channelId: string
  /** Old-comment count for the channel, capped at the backend limit. */
  count: number
  /** Backend cap applied to `count` (so callers can render "N+"). */
  limit: number
}

type UseOldCommentsCheckReturn = {
  hasOldComments: boolean
  /** Per-channel breakdown, limited to channels that have old comments. */
  channelsWithOldComments: ChannelWithOldComments[]
  isLoading: boolean
  error: Error | null
}

/* Comment events that should trigger a recheck */
const relevantEvents = [
  PusherEvent.COMMENT_REPLIED,
  PusherEvent.COMMENT_DISMISSED,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_POST,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_CHANNEL,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_ORGANIZATION,
  PusherEvent.DISMISS_ALL_COMMENTS_FOR_CHANNELS,
]

/**
 * Hook to check which channels have unreplied comments older than one month.
 * Powers the MonthlyCleanupBanner: whether to show it, and (via the per-channel
 * breakdown) whether to run the single- or multi-channel cleanup flow.
 *
 * This hook subscribes to relevant Pusher events to automatically refetch when comments
 * are replied to or dismissed, ensuring the banner hides in real-time.
 *
 * @param params - Object containing channelIds array
 * @returns Object with hasOldComments boolean, loading state, and error state
 *
 * @example
 * const { hasOldComments, isLoading } = useOldCommentsCheck({
 *   channelIds: ['channel-123', 'channel-456']
 * })
 */
export function useOldCommentsCheck({
  channelIds,
}: UseOldCommentsCheckParams): UseOldCommentsCheckReturn {
  const [channelsWithOldComments, setChannelsWithOldComments] = useState<
    ChannelWithOldComments[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const organizationId = useOrganizationId()
  const { fetchCounts } = useCommentsCounts()

  // Stabilize channelIds array reference to prevent infinite loops
  const stableChannelIds = useStableArray(channelIds)

  // Monotonic id so a slow, older check can't overwrite the result of a newer
  // one (Pusher events can fire several checks in quick succession).
  const requestIdRef = useRef(0)

  const checkOldComments = useCallback(async (): Promise<void> => {
    if (!organizationId || stableChannelIds.length === 0) {
      setChannelsWithOldComments([])
      return
    }

    const requestId = ++requestIdRef.current
    // Recompute the cutoff per check, not once at mount, so a long-lived session
    // (or a recheck that crosses a day/month boundary) always queries against the
    // current "one month ago".
    const dateEnd = getOneMonthAgoIso()

    setIsLoading(true)
    setError(null)

    try {
      const result = await fetchCounts({
        organizationId,
        filters: {
          channelIds: stableChannelIds,
          status: ['unreplied'],
          publishedAt: {
            end: dateEnd,
          },
        },
        // Fetch real per-channel counts so the cleanup flow can list each
        // channel and total the comments it will resolve.
        limit: RESOLVE_COMMENTS_LIMIT,
      })

      // Drop a response that resolved after a newer check started.
      if (requestId !== requestIdRef.current) return

      const counts = result.data?.commentsCounts?.counts ?? []
      setChannelsWithOldComments(
        counts
          .filter((item) => (item.count ?? 0) > 0)
          .map((item) => ({
            channelId: item.channelId,
            count: item.count ?? 0,
            limit: item.limit ?? RESOLVE_COMMENTS_LIMIT,
          })),
      )
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      const normalizedError =
        err instanceof Error ? err : new Error('Unknown error')
      setError(normalizedError)
      setChannelsWithOldComments([])
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false)
    }
  }, [fetchCounts, organizationId, stableChannelIds])

  // Initial fetch
  useEffect(() => {
    void checkOldComments()
  }, [checkOldComments])

  // Subscribe to relevant Pusher events to refetch when comments are replied/dismissed
  usePusherEvent(relevantEvents, (data: { channelId?: string }) => {
    // Only refetch if the event is for one of our channels or affects all channels
    if (
      !data.channelId ||
      stableChannelIds.length === 0 ||
      stableChannelIds.includes(data.channelId)
    ) {
      void checkOldComments()
    }
  })

  return {
    hasOldComments: channelsWithOldComments.length > 0,
    channelsWithOldComments,
    isLoading,
    error,
  }
}
